import React, { useState } from 'react';
import './ApplicationForm.css';

const ApplicationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    location: '',
    pitchDeck: null
  });

    const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [uploadProgress, setUploadProgress] = useState('');
  
  // Your deployed API Gateway URL
  const API_BASE_URL = 'https://dl6poek2ig.execute-api.us-east-1.amazonaws.com/prod';

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:application/...;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };



  const handleFileChange = (file) => {
    if (file) {
      // Check if file is PDF
      const allowedTypes = [
        'application/pdf'
      ];
      
      if (allowedTypes.includes(file.type) || file.name.toLowerCase().endsWith('.pdf')) {
        setFormData(prev => ({
          ...prev,
          pitchDeck: file
        }));
        
        // Clear error
        if (errors.pitchDeck) {
          setErrors(prev => ({
            ...prev,
            pitchDeck: ''
          }));
        }
      } else {
        setErrors(prev => ({
          ...prev,
          pitchDeck: 'Please upload a PDF file (.pdf)'
        }));
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }



    if (!formData.pitchDeck) {
      newErrors.pitchDeck = 'Pitch deck is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Upload file directly to S3 using pre-signed URL
  const uploadToS3 = async (file, uploadUrl) => {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const file = formData.pitchDeck;
      const fileSizeMB = file.size / 1024 / 1024;
      
      console.log(`Uploading file (${fileSizeMB.toFixed(2)}MB) using S3 signed URL`);
      setUploadProgress('Getting upload URL...');
      
      // Step 1: Get pre-signed URL from Lambda
      const uploadUrlResponse = await fetch(`${API_BASE_URL}/get-upload-s3?email=${encodeURIComponent(formData.email.trim())}&file_name=${encodeURIComponent(file.name)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const uploadUrlResult = await uploadUrlResponse.json();

      if (!uploadUrlResponse.ok || !uploadUrlResult.upload_url) {
        throw new Error(uploadUrlResult.error || 'Failed to get upload URL');
      }

      // Step 2: Upload directly to S3
      setUploadProgress(`Uploading ${fileSizeMB.toFixed(1)}MB file...`);
      console.log('Uploading file directly to S3...');
      await uploadToS3(file, uploadUrlResult.upload_url);

      console.log('File upload completed successfully');
      
      // Step 3: Submit application with S3 key
      setUploadProgress('Submitting application...');
      const submitResponse = await fetch(`${API_BASE_URL}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          location: formData.location.trim(),
          s3Key: uploadUrlResult.s3_key
        })
      });

      const submitResult = await submitResponse.json();

      if (!submitResponse.ok || !submitResult.success) {
        throw new Error(submitResult.error || 'Failed to submit application');
      }

      console.log('Application submitted successfully');
      setUploadProgress('');
      
      // Set success status
      setSubmitStatus('success');
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setUploadProgress('');
      setErrors({ submit: error.message || 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  return (
    <div className="application-container">
      <div className="background-grid"></div>
      <div className="application-form">
        <div className="form-header">
          <h1 className="form-title">Submit an Application</h1>
          <div className="title-underline"></div>
          <p className="form-subtitle">Join the future of AI innovation. Submit your application to our incubator program.</p>
        </div>
        
        {submitStatus === 'success' && (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <div>
              <h3>Application Submitted Successfully!</h3>
              <p>Thank you for submitting your application. We'll review it and get back to you within 48 hours.</p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && errors.submit && (
          <div className="error-banner">
            <div className="error-icon">⚠</div>
            <div>
              <h3>Submission Failed</h3>
              <p>{errors.submit}</p>
            </div>
          </div>
        )}

        {!submitStatus && (
          <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`form-input ${errors.firstName ? 'error' : ''}`}
                placeholder="Enter your first name"
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`form-input ${errors.lastName ? 'error' : ''}`}
                placeholder="Enter your last name"
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="Enter your email address"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="location" className="form-label">
              Location *
            </label>
            <select
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className={`form-input ${errors.location ? 'error' : ''}`}
            >
              <option value="">Select your location</option>
              <option value="Berlin">Berlin</option>
              <option value="Cambridge">Cambridge</option>
              <option value="San Francisco">San Francisco</option>
              <option value="Tokyo">Tokyo</option>
            </select>
            {errors.location && <span className="error-message">{errors.location}</span>}
          </div>



          <div className="form-group">
            <label htmlFor="pitchDeck" className="form-label">
              Pitch Deck (PDF) *
            </label>
            <div 
              className={`file-upload-wrapper ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="pitchDeck"
                name="pitchDeck"
                onChange={(e) => handleFileChange(e.target.files[0])}
                accept=".pdf,application/pdf"
                className="file-input"
              />
              <label htmlFor="pitchDeck" className="file-upload-label">
                <div className="upload-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="upload-text">
                  {formData.pitchDeck ? (
                    <>
                      <span className="file-name">{formData.pitchDeck.name}</span>
                      <span className="file-size">({(formData.pitchDeck.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </>
                  ) : (
                    <>
                      <span className="upload-primary">Drop your PDF here</span>
                      <span className="upload-secondary">or click to browse</span>
                    </>
                  )}
                </div>
              </label>
            </div>
            {errors.pitchDeck && <span className="error-message">{errors.pitchDeck}</span>}
          </div>

          <button 
            type="submit" 
            className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="spinner"></div>
                {uploadProgress || 'Processing Application...'}
              </>
            ) : (
              <>
                Submit Application
                <svg className="submit-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ApplicationForm;
